import {toCamelCase, isElement, warn} from '../helpers/_utilities';
import {$data} from '../helpers/data';
import is from 'is_js';
import {$events} from '../helpers/events';
import Model from './Model';
import Component from './Component';
import {$dom} from '../helpers/dom';
import {$style} from '../helpers/style';
import {$ui} from '../helpers/ui';
import variables from "../variables";
import {Offcanvas, Modal, Collapse, Dropdown} from "bootstrap";

const {merge} = $data;
const {customEventNames} = variables;

export default class Page extends Model {
	#defaults = {
		debug: false
	};

	#assetsPathAttr = 'data-assets-path';
	#assetsPathAsDataset = toCamelCase(this.#assetsPathAttr.replace('data-', ''));

	constructor(options) {
		super(options);

		this.options = merge(this.#defaults, options);

		if (is.not.domNode(this.rootEl)) return;

		this.components = {};
		this.resizeDependentMethods = [];
		this.helpers = {
			'dom': $dom,
			'is': is,
			'events': $events,
			'style': $style,
			'ui': $ui
		};

		this.assetsPath = this.rootEl.dataset[`${this.#assetsPathAsDataset}`];

		if (is.undefined(this.assetsPath)) {
			this.options.debug && warn(
				`Path to svg-sprite is not provided to root element as "${this.#assetsPathAttr}" attribute`,
				'Class Page, constructor'
			)
		} else {
			this.rootEl.removeAttribute(`${this.#assetsPathAttr}`)
		}
	}

	get rootEl(){
		const
			{rootElementId} = this.options,
			el = document.getElementById(rootElementId)
		;

		if (is.string(rootElementId) && Boolean(rootElementId) && isElement(el)) return el;

		return null
	};

	addComponent(fn, initialArgs = []) {
		if (is.function(fn) ) {

			const component = fn.call(this, ...initialArgs);

            if (component?.options?.name && is.undefined(this.components[component.options.name])) {
                if (!component.options.isPrivate)
                    this.components[component.options.name] = component;

                if (!component?.initialized && is.function(component.init)) component.init()
            }
		}

		return this;
	}

	destroyComponent(name) {
		if (this.components.hasOwnProperty(name)) {
			const component = this.components[name];

			if (component instanceof Component && component.initialized && is.function(component.destroy)) {
				component.destroy();

				delete this.components[name];
			}
		}

		return this;
	};

	removeComponents(arg) {
		if (is.undefined(arg)) {
			Object.entries(this.components).forEach(([name, component]) => this.destroyComponent(name));
		} else if (is.array(arg) && arg.length > 0) {
			arg.forEach(componentName => this.destroyComponent(componentName))
		} else if (is.string(arg) && arg.length > 0) {
			this.destroyComponent(arg)
		}

		return this;
	}

	addResizeDependentMethod(method) {
		if (is.function(method)) {

			if (this.resizeDependentMethods.indexOf(method) < 0) {
				$events.resize('on', method);
				this.resizeDependentMethods.push(method);
			}

		}
		return this
	}

	removeResizeDependentMethod(method) {

		if (is.undefined(method)) {
			if (this.resizeDependentMethods.length > 0) {
				this.resizeDependentMethods = this.resizeDependentMethods.filter(m => {
					$events.resize('off', m);
					return false
				})
			}
			return this;
		}

		const indOf = this.resizeDependentMethods.indexOf(method);
		if (indOf !== -1) {
			this.resizeDependentMethods = this.resizeDependentMethods.splice(indOf, 1);
			$events.resize('off', method);
		}

		return this
	}

	preventClickDisabledLinks(e) {e.preventDefault()}

    blockInvalidNumberChar(e) {
        ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()
    };

	init(usedComponents = []) {
        $events.delegate
            .on('click', 'a.is-disabled', this.preventClickDisabledLinks)
            .on('keydown', 'input[type="number"]', this.blockInvalidNumberChar)
        ;

        this.bootstrap = {Modal, Collapse, Dropdown, Offcanvas};

        usedComponents.forEach(c => {
            try {
                this.addComponent(c)
            } catch (e) {console.error(e)}
        });

        window.recaptchaOnloadCallback = () => $events.emit(customEventNames.recaptchaLoaded);

        super.init(this);
		return this;
	}

	destroy() {
        window.recaptchaOnloadCallback = null;

		try {
            $events.delegate
                .off('click', 'a.is-disabled', this.preventClickDisabledLinks)
                .off('keydown', 'input[type="number"]', this.blockInvalidNumberChar)
            ;

			this.removeComponents();
			this.removeResizeDependentMethod();

			super.destroy(this);
		} catch (e) {console.error(e)}

		return this;
	}
}
